/** Speech bubble near a raid unit (Phaser glue — no sim). */

import Phaser from "phaser";
import { hexToColor } from "../../../data/raid-catalog";
import { RaidDepth } from "./raid-layers";

export type SpeechBubbleStyle = {
  offsetY: number;
  durationMs: number;
  fontSize: number;
  maxWidth: number;
  padX: number;
  padY: number;
  bg: string;
  border: string;
  text: string;
  tailH: number;
};

export const DEFAULT_SPEECH_BUBBLE: SpeechBubbleStyle = {
  offsetY: -42,
  durationMs: 2200,
  fontSize: 12,
  maxWidth: 118,
  padX: 8,
  padY: 5,
  bg: "#1c1c28",
  border: "#e8dcc8",
  text: "#f4efe6",
  tailH: 6,
};

export type SpeechBubbleHandle = {
  unitId: number;
  root: Phaser.GameObjects.Container;
  destroy: () => void;
};

export function playSpeechBubble(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  unitId: number,
  text: string,
  style: Partial<SpeechBubbleStyle> = {},
): SpeechBubbleHandle {
  const s = { ...DEFAULT_SPEECH_BUBBLE, ...style };
  const label = scene.add
    .text(0, 0, text, {
      fontFamily: "Malgun Gothic, sans-serif",
      fontSize: `${s.fontSize}px`,
      color: s.text,
      align: "center",
      wordWrap: { width: s.maxWidth },
    })
    .setOrigin(0.5, 1);

  const tw = Math.max(label.width, 24);
  const th = Math.max(label.height, s.fontSize);
  const bw = tw + s.padX * 2;
  const bh = th + s.padY * 2;
  const bg = scene.add.graphics();
  const fill = hexToColor(s.bg);
  const stroke = hexToColor(s.border);
  bg.fillStyle(fill, 0.92);
  bg.lineStyle(1.5, stroke, 0.95);
  bg.fillRoundedRect(-bw / 2, -bh - s.tailH, bw, bh, 6);
  bg.strokeRoundedRect(-bw / 2, -bh - s.tailH, bw, bh, 6);
  bg.fillTriangle(-5, -s.tailH, 5, -s.tailH, 0, 0);

  label.setPosition(0, -s.tailH - s.padY);

  const root = scene.add.container(0, s.offsetY, [bg, label]);
  parent.add(root);
  root.setDepth(RaidDepth.float + 1);

  const tween = scene.tweens.add({
    targets: root,
    alpha: 0,
    delay: Math.max(0, s.durationMs - 400),
    duration: 400,
    onComplete: () => {
      if (root.active) root.destroy(true);
    },
  });

  return {
    unitId,
    root,
    destroy: () => {
      tween.stop();
      if (root.active) root.destroy(true);
    },
  };
}
